import { useState } from "react";
import { Link } from "react-router-dom";
import { BACKEND_URL } from "@/lib/api";
import { Heart, Eye, Share2, Star, Zap } from "lucide-react";
import RugProofBadge from "@/components/RugProofBadge";
import VerifiedBadge from "@/components/VerifiedBadge";
import WatchButton from "@/components/WatchButton";

const themeColor = {
  meme: "#FF3E9D",
  utility: "#00D4FF",
  gaming: "#A855F7",
  ai: "#CFFF04",
  defi: "#FFD700",
};

// Status banners + identity card + like/share/views for a coin's /c/:slug page.
export default function CoinHeader({ coin, wp, likes, liked, onLike, onShare }) {
  const isPublished = coin.status === "published" || coin.status === "featured";
  const [logoFailed, setLogoFailed] = useState(false);
  const accent = coin.accent_color || themeColor[coin.theme] || "#cfff04";
  return (
    <>
      {!isPublished && (
        <div
          className="mb-6 border border-[#ffd700] bg-[#1a1605] p-4 flex items-center justify-between gap-4 flex-wrap"
          data-testid="draft-banner"
        >
          <div className="font-mono text-xs text-[#ffd700]">
            ⚠ DRAFT — not listed publicly. Publish to share + appear on /explore.
          </div>
          <Link
            to={`/create?resume=${coin.slug}`}
            data-testid="draft-resume-link"
            className="btn-brutal bg-[#cfff04] text-[#050505] font-bold uppercase tracking-wider text-xs px-4 py-2"
          >
            Resume publish →
          </Link>
        </div>
      )}
      {coin.status === "featured" && (
        <div className="mb-6 bg-[#cfff04] text-[#050505] px-4 py-2 font-mono text-xs uppercase tracking-widest flex items-center gap-2">
          <Star size={12} fill="currentColor" /> Featured launch · top of /explore
        </div>
      )}

      <div className="grid grid-cols-12 gap-6 mb-8">
        <div className="col-span-12 md:col-span-8 flex items-center gap-5">
          <div
            className="w-20 h-20 sm:w-28 sm:h-28 flex-shrink-0 border-2 bg-[#050505] overflow-hidden"
            style={{ borderColor: accent }}
          >
            {logoFailed ? (
              <div
                className="w-full h-full flex items-center justify-center font-display font-black text-4xl sm:text-5xl"
                style={{ color: accent }}
                data-testid="coin-logo-fallback"
              >
                {(coin.ticker || "?").charAt(0)}
              </div>
            ) : (
              <img
                src={`${BACKEND_URL}/api/coins/${coin.slug}/logo`}
                alt={coin.name}
                className="w-full h-full object-cover"
                onError={() => setLogoFailed(true)}
              />
            )}
          </div>
          <div className="min-w-0">
            <h1
              className="font-display font-black text-3xl sm:text-5xl uppercase tracking-tighter break-words"
              data-testid="coin-name"
            >
              {coin.name}
            </h1>
            <div className="font-mono text-sm text-[#e2e2e2] mt-1">
              <span className="text-[#cfff04]">${coin.ticker}</span> · {coin.theme} ·{" "}
              {Number(coin.supply).toLocaleString()} supply
              {coin.mint_address && (
                <a
                  href={`https://solscan.io/token/${coin.mint_address}`}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="coin-onchain-badge"
                  className="ml-2 inline-flex items-center gap-1 border border-[#00ff41]/50 text-[#00ff41] px-2 py-0.5 text-[11px] uppercase tracking-widest hover:bg-[#00ff41]/10"
                >
                  ✓ On-chain
                </a>
              )}
              <span className="ml-2 inline-flex align-middle gap-2"><VerifiedBadge coin={coin} /><RugProofBadge coin={coin} /></span>
              {coin.x_handle && (
                <a
                  href={`https://x.com/${coin.x_handle}`}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="coin-x-handle"
                  className="ml-2 text-[#e2e2e2] hover:text-white"
                >
                  @{coin.x_handle}
                </a>
              )}
              {coin.og_badge && (
                <span
                  className="ml-2 inline-flex items-center gap-1 border border-[#FFD700]/60 text-[#FFD700] px-2 py-0.5 text-[11px] uppercase tracking-widest"
                  data-testid="og-badge"
                >
                  ★ OG
                </span>
              )}
            </div>
            {wp.tagline && (
              <div className="mt-3 text-sm sm:text-base text-white italic" data-testid="coin-tagline">
                "{wp.tagline}"
              </div>
            )}
          </div>
        </div>
        <div className="col-span-12 md:col-span-4 flex md:justify-end items-start gap-2 flex-wrap">
          <button
            onClick={onLike}
            data-testid="coin-like"
            className={`border px-4 py-2.5 font-mono text-xs uppercase tracking-widest flex items-center gap-2 ${
              liked
                ? "border-[#ff3e9d] text-[#ff3e9d]"
                : "border-[#3a3a3a] text-[#e2e2e2] hover:border-[#cfff04] hover:text-[#cfff04]"
            }`}
          >
            <Heart size={12} fill={liked ? "currentColor" : "none"} /> {likes}
          </button>
          <button
            onClick={onShare}
            data-testid="coin-share"
            className="border border-[#3a3a3a] text-[#e2e2e2] hover:border-[#cfff04] hover:text-[#cfff04] px-4 py-2.5 font-mono text-xs uppercase tracking-widest flex items-center gap-2"
          >
            <Share2 size={12} /> Share
          </button>
          {isPublished && <WatchButton coin={coin} />}
          {coin.mint_address && coin.lp_pool_id && (
            <a
              href={`https://jup.ag/swap/SOL-${coin.mint_address}`}
              target="_blank"
              rel="noreferrer"
              data-testid="coin-buy-jupiter"
              className="btn-brutal bg-[#00ff41] text-[#050505] font-bold px-4 py-2.5 font-mono text-xs uppercase tracking-widest flex items-center gap-2"
            >
              <Zap size={12} fill="currentColor" /> Buy
            </a>
          )}
          <div className="flex items-center gap-1 font-mono text-[11px] text-[#d0d0d0] uppercase tracking-widest border border-[#3a3a3a] px-3 py-2.5">
            <Eye size={11} /> {coin.views || 0}
          </div>
        </div>
      </div>
    </>
  );
}
